import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { fetchProducts } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCategories } from "@/context/CategoriesContext";
import ProductCard from "@/components/ProductCard";

const Home = () => {
  const { lang } = useLang();
  const { categories, label } = useCategories();
  const { data: products = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
  const [filter, setFilter] = useState("ALL");
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();

  const shown = useMemo(() => {
    let list = filter === "ALL" ? products : products.filter((p) => p.category === filter);
    if (q) list = list.filter((p) => `${p.name_ro} ${p.name_en} ${p.code}`.toLowerCase().includes(q));
    // In TOATE, group products by the admin-defined category order (stable within a category)
    if (filter === "ALL") {
      const order = new Map(categories.map((c, i) => [c.slug, i]));
      list = [...list].sort((a, b) => (order.get(a.category) ?? 99) - (order.get(b.category) ?? 99));
    }
    return list;
  }, [products, filter, q, categories]);

  const allLabel = lang === "ro" ? "TOATE" : "ALL";
  const cats = categories.filter((c) => products.some((p) => p.category === c.slug));

  return (
    <main data-testid="home-page">
      <div className="sticky top-[52px] z-30 flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-border bg-white/90 px-4 py-3 backdrop-blur-md sm:px-6">
        <button onClick={() => setFilter("ALL")} data-testid="filter-all"
          className={`font-mono text-[11px] uppercase tracking-widest transition-opacity ${filter === "ALL" ? "opacity-100 underline underline-offset-4" : "opacity-45 hover:opacity-100"}`}>
          {allLabel} [{products.length}]
        </button>
        {cats.map((c) => (
          <button key={c.slug} onClick={() => setFilter(c.slug)} data-testid={`filter-${c.slug}`}
            className={`font-mono text-[11px] uppercase tracking-widest transition-opacity ${filter === c.slug ? "opacity-100 underline underline-offset-4" : "opacity-45 hover:opacity-100"}`}>
            {label(c.slug, lang)}
          </button>
        ))}
        {q && <span className="ml-auto font-mono text-[11px] uppercase tracking-widest text-muted-foreground">„{q}" [{shown.length}]</span>}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 border-t border-border md:grid-cols-3 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] animate-pulse border-b border-r border-border bg-secondary" />)}
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-2 border-t border-border md:grid-cols-3 lg:grid-cols-4">
          {shown.map((p) => <ProductCard key={p.id} product={p} />)}
        </motion.div>
      )}
    </main>
  );
};

export default Home;
